import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { AppStateService } from '../app-state/app-state.service';
import { API_BASE, PROJECT } from '../../constants/general.constants';
import {
  PushDestination,
  CreatePushDestinationRequest,
  UpdatePushDestinationRequest,
  PushDestinationTestResult
} from '../../interfaces/push-destination.interface';

@Injectable({
  providedIn: 'root'
})
export class PushDestinationService {
  readonly #httpClient = inject(HttpClient);
  readonly #appState = inject(AppStateService);

  baseUrl = computed(() => this.buildUrl(this.#appState.projectId()));

  readonly #destinations = signal<PushDestination[]>([]);
  destinations = this.#destinations.asReadonly();

  private buildUrl(projectId: number, destinationId?: number): string {
    const baseUrl = `${API_BASE}/${PROJECT}/${projectId}/push-destinations`;
    return destinationId ? `${baseUrl}/${destinationId}` : baseUrl;
  }

  private executeRequest<T>(
    projectId: number,
    request: Observable<T>,
    updateFn?: (result: T, destinations: PushDestination[]) => PushDestination[]
  ): Observable<T> {
    return request.pipe(
      tap((result) => {
        if (projectId === this.#appState.projectId() && updateFn) {
          this.#destinations.set(updateFn(result, this.#destinations()));
        }
      })
    );
  }

  list(projectId: number): Observable<PushDestination[]> {
    if (!projectId || projectId <= 0) {
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID: ${projectId}`));
      });
    }

    const url = this.buildUrl(projectId);
    return this.executeRequest<PushDestination[]>(
      projectId,
      this.#httpClient.get<PushDestination[]>(url),
      (destinations) => destinations
    );
  }

  create(projectId: number, request: CreatePushDestinationRequest): Observable<PushDestination> {
    if (!projectId || projectId <= 0) {
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID: ${projectId}`));
      });
    }

    const url = this.buildUrl(projectId);
    return this.executeRequest<PushDestination>(
      projectId,
      this.#httpClient.post<PushDestination>(url, request),
      (destination, destinations) => [...destinations, destination]
    );
  }

  update(projectId: number, destinationId: number, request: UpdatePushDestinationRequest): Observable<PushDestination> {
    if (!projectId || projectId <= 0 || !destinationId || destinationId <= 0) {
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID or destination ID`));
      });
    }

    const url = this.buildUrl(projectId, destinationId);
    return this.executeRequest<PushDestination>(
      projectId,
      this.#httpClient.put<PushDestination>(url, request),
      (updated, destinations) => {
        const idx = destinations.findIndex((d) => d.destinationId === destinationId);
        if (idx >= 0) {
          const result = [...destinations];
          result[idx] = updated;
          return result;
        }
        return destinations;
      }
    );
  }

  delete(projectId: number, destinationId: number): Observable<void> {
    if (!projectId || projectId <= 0 || !destinationId || destinationId <= 0) {
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID or destination ID`));
      });
    }

    const url = this.buildUrl(projectId, destinationId);
    return this.executeRequest<void>(
      projectId,
      this.#httpClient.delete<void>(url),
      (_, destinations) => destinations.filter((d) => d.destinationId !== destinationId)
    );
  }

  test(projectId: number, destinationId: number): Observable<PushDestinationTestResult> {
    if (!projectId || projectId <= 0 || !destinationId || destinationId <= 0) {
      return new Observable(subscriber => {
        subscriber.error(new Error('Invalid project or destination ID for test'));
      });
    }

    const url = `${this.buildUrl(projectId, destinationId)}/test`;
    return this.#httpClient.post<PushDestinationTestResult>(url, {});
  }

  /**
   * Send a script-triggered ("Manual") notification to a destination by name.
   * Backs EHQ.sendNotification(). Delivery is async/rate-limited server-side;
   * a 202 response means the notification was queued.
   */
  send(
    projectId: number,
    request: { destination: string; message: string; options?: Record<string, unknown> }
  ): Observable<{ status: string; destination: string }> {
    if (!projectId || projectId <= 0) {
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID: ${projectId}`));
      });
    }

    const url = `${this.buildUrl(projectId)}/send`;
    return this.#httpClient.post<{ status: string; destination: string }>(url, request);
  }

  getCurrentProjectId(): number {
    return this.#appState.projectId();
  }

  hasValidProjectId(): boolean {
    const projectId = this.#appState.projectId();
    return !!(projectId && projectId > 0);
  }
}
